import { useEffect, useState } from "react";
import styles from "./styles/home.module.scss";
import layoutStyles from '../../styles/layout.module.scss';
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/useI18n";
import { Icon } from "../../assets/icons";
import { OptionsHomeTasks } from "./components/OptionsHomeTasks";
import { WelcomeHeader } from "./components/WelcomeHeader";
import { WeeklyTasksSummary } from "./components/WeeklyTasksSummary";
import { TitaCoinsShowcase } from "./components/TitaCoinsShowcase";
import { useDispatch } from "react-redux";
import { useGetAllCompanies } from "../users/hooks/useGetAllCompanies";
import { loadCompanies } from "../companies/slices/companiesSlice";
import { useGetAllTeams } from "../users/hooks/useGetAllTeams";
import { getItem } from "../authentication/utils/handleLocalStorage";
import { loadTeams } from "../users/slices/teamSlice";
import HomeTaskCard from "./components/HomeTaskCard";
import { ITask } from "@/api/tasks/types";
import { TaskDetailDrawer } from "@/components/common/TaskDetailDrawer";
import { useGetTaskAssignmentsWithSchedules } from "@/features/assignments/hooks/timeAssignments/useGetTaskAssignmentsWithSchedules";
import { formatInTimeZone } from 'date-fns-tz';
import { Assignment } from "@/api/timeAssignments/types";

// Helper function to parse time strings (HH:mm:ss) and convert to hours
function parseTimeToHours(timeString: string | number): number {
  if (timeString === null || timeString === undefined) return 0;
  
  if (typeof timeString === 'number') {
    return timeString;
  }
  
  if (!timeString.includes(':')) {
    const num = parseFloat(timeString);
    return isNaN(num) ? 0 : num;
  }
  
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
}

export const Home = () => {
  console.log("🔥 HOME COMPONENT RENDERED WITH UPDATED CODE - " + new Date().toISOString());
  
  const { t } = useI18n();
  const token = getItem("token");
  const { data: companiesData, isSuccess: isSuccessCompanies } = useGetAllCompanies();
  const { data: teamsData, isSuccess } = useGetAllTeams({ enabled: !!token });
  const dispatch = useDispatch();

  const [tasks, setTasks] = useState<ITask[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<ITask['id'] | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Add a state to force refresh
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // State for time assignments map
  const [timeAssignmentsMap, setTimeAssignmentsMap] = useState<Map<string, { estimatedTime: number; realTime: number }>>(new Map());
  
  // Define GMT-5 timezone
  const timeZone = 'America/Bogota'; // Represents GMT-5
  const today = new Date();
  
  // Format date as YYYY-MM-DD for comparison
  const formattedDate = formatInTimeZone(today, timeZone, 'yyyy-MM-dd');

  // Use the same date for both parameters to get only today's assignments
  const { data: assignmentsData, isLoading: areAssignmentsLoading, isError, error } = useGetTaskAssignmentsWithSchedules(formattedDate, formattedDate, refreshTrigger);

  // Force a refresh of the component when the date changes
  useEffect(() => {
    const interval = setInterval(() => {
      const newFormattedDate = formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd');
      if (newFormattedDate !== formattedDate) {
        console.log("📅 Date changed, refreshing component");
        window.location.reload();
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [formattedDate, timeZone]);

  useEffect(() => {
    if (isError) {
      console.error("Error fetching assignments:", error);
    }
    if (assignmentsData) {
      const tasksFromAssignments: ITask[] = [];
      const timeMap = new Map<string, { estimatedTime: number; realTime: number }>();
      
      if (assignmentsData.assignments) {
        for (const assignment of assignmentsData.assignments as Assignment[]) {
          if (!assignment.task_id || !assignment.task_title) {
            continue;
          }
          
          let hasEstimatedTimeForToday = false;
          let totalEstimated = 0;
          let totalReal = 0;
          
          if (assignment.schedules) {
            for (const schedule of assignment.schedules) {
              let scheduleDateStr = '';
              if (typeof schedule.scheduleDate === 'string') {
                scheduleDateStr = schedule.scheduleDate.split('T')[0];
              }
              
              if (scheduleDateStr === formattedDate) {
                const estimatedTime = parseTimeToHours(schedule.estimated_time);
                const realTime = parseTimeToHours(schedule.real_time);
                
                totalEstimated += estimatedTime;
                totalReal += realTime;
                
                if (estimatedTime > 0) {
                  hasEstimatedTimeForToday = true;
                }
              }
            }
          }
          
          if (hasEstimatedTimeForToday) {
            const taskId = String(assignment.task_id);
            timeMap.set(taskId, {
              estimatedTime: totalEstimated,
              realTime: totalReal
            });
            
            const task: ITask = {
              id: taskId,
              title: assignment.task_title || 'Sin título',
              projectId: assignment.project_id ? String(assignment.project_id) : '',
              sectionId: '',
              state: 'incompleta',
              startDate: undefined,
              endDate: undefined,
              description: ''
            };
            
            if (!tasksFromAssignments.some(t => t.id === taskId)) {
              tasksFromAssignments.push(task);
            }
          }
        }
      }
      
      setTasks(tasksFromAssignments);
      setTimeAssignmentsMap(timeMap);
    }
  }, [assignmentsData, formattedDate, isError, error]);

  // Force refresh of assignments data
  const forceRefresh = () => {
    console.log("🔄 Forcing refresh of assignments data");
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const getData = async () => {
      if (companiesData && isSuccessCompanies) {
        dispatch(loadCompanies(companiesData));
      }
      if (teamsData && isSuccess) {
        dispatch(loadTeams(teamsData));
      }
    };
    getData();
  }, [companiesData, isSuccessCompanies, teamsData, isSuccess, dispatch]);

  const [visibility, setVisibility] = useState({
    tasksToDeliver: true,
    tasksExpire: true,
    allTasks: false,
  });

  const setTaksVilivility = (data: string) => {
    switch (data) {
      case "tasksToDeliver":
        setVisibility(prev => ({ ...prev, tasksToDeliver: !prev.tasksToDeliver }));
        break;
      case "tasksExpire":
        setVisibility(prev => ({ ...prev, tasksExpire: !prev.tasksExpire }));
        break;
      case "allTasks":
        setVisibility(prev => ({ ...prev, allTasks: !prev.allTasks }));
        break;
      default:
        setVisibility(prev => prev);
    }
  }

  const renderTasks = (type: 'today' | 'due' | 'all') => {
    let filteredTasks: ITask[] = [];

    if (type === 'today') {
      if (areAssignmentsLoading) {
        return (
          <div className={styles.no_tasks_container}>
            <p>Cargando tareas de hoy...</p>
            <div className={styles.loading_indicator}>
              <span className={styles.loader}></span>
            </div>
          </div>
        );
      }
      
      if (isError) {
        return (
          <div className={styles.no_tasks_container}>
            <p>No se pudieron cargar las tareas de hoy.</p>
            <button 
              className={styles.refresh_button}
              onClick={forceRefresh}
            >
              🔄 Reintentar
            </button>
          </div>
        );
      }
      
      // For today's tasks, just use the tasks we built from assignments
      filteredTasks = tasks;
      
      console.log(`📊 Mostrando ${filteredTasks.length} tareas para hoy`);
      
      if (filteredTasks.length === 0) {
        return (
          <div className={styles.no_tasks_container}>
            <p>No hay tareas programadas para hoy con tiempo estimado.</p>
            <button 
              className={styles.refresh_button}
              onClick={forceRefresh}
            >
              🔄 Recargar datos
            </button>
          </div>
        );
      }
    } else if (type === 'due') {
      filteredTasks = tasks.filter((t: ITask) => {
        if (!t.endDate) return false;
        // The comparison for 'due' tasks should also be timezone-aware if needed
        const endDate = new Date(t.endDate);
        return endDate < today;
      });
    } else {
      filteredTasks = tasks;
    }

    return (
      <div className={styles.tasks_container}>
        {filteredTasks.map((task: ITask) => {
          const timeData = timeAssignmentsMap.get(task.id);
          const taskWithTime = {
            ...task,
            estimatedTime: timeData?.estimatedTime,
            realTime: timeData?.realTime,
          };

          return (
            <HomeTaskCard
              key={task.id}
              task={taskWithTime}
              onClick={() => handleTaskClick(task)}
            />
          );
        })}
      </div>
    );
  }

  const handleTaskClick = (task: ITask) => {
    setSelectedTaskId(task.id);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTaskId(null);
  };

  return (
    <>
      <WelcomeHeader />
      <WeeklyTasksSummary onTaskClick={handleTaskClick} />
      <TitaCoinsShowcase />
      <div className={`${layoutStyles.content__divHome}`}>
        <div>
          <Icon name="arrowDownHome" color="#AAAAAA" />
          <span className={`${layoutStyles.content__label}`}>{t("home_page.initial_title")}</span>
        </div>
        <div className={`${styles.options_inicio_tasks__content_button}`}>
          <Link className={`${styles.options_inicio_tasks__button}`} to="/assingment">
            <Icon name="campaing" color="#fff" />
            {t("home_page.button_show_tasks")}
          </Link>
          <button 
            className={`${styles.options_inicio_tasks__button} ${styles.refresh_button}`}
            onClick={forceRefresh}
            disabled={areAssignmentsLoading}
          >
            {areAssignmentsLoading ? 'Cargando...' : '🔄 Recargar'}
          </button>
        </div>
      </div>

      <div className={styles.status_indicator}>
        {areAssignmentsLoading ? (
          <div className={styles.loading_message}>
            <span className={styles.loader_small}></span>
            <span>Cargando asignaciones...</span>
          </div>
        ) : (
          <div className={styles.date_info}>
            <span>Fecha: {formatInTimeZone(new Date(), timeZone, 'dd/MM/yyyy')}</span>
            <span className={styles.task_count}>
              {tasks.length > 0 ? `${tasks.length} tareas programadas para hoy` : 'No hay tareas programadas para hoy'}
            </span>
          </div>
        )}
      </div>

      <OptionsHomeTasks
        visibilityTasks={visibility.tasksToDeliver}
        setTaksVilivility={setTaksVilivility}
        taksassing="tasksToDeliver"
        text={t("home_page.pending_task_title")}
      />
      {visibility.tasksToDeliver && renderTasks('today')}
      <OptionsHomeTasks
        visibilityTasks={visibility.tasksExpire}
        setTaksVilivility={setTaksVilivility}
        taksassing={'tasksExpire'}
        text={t("home_page.that_are_due_title")}
      />
      {visibility.tasksExpire && renderTasks('due')}
      <OptionsHomeTasks
        visibilityTasks={visibility.allTasks}
        setTaksVilivility={setTaksVilivility}
        taksassing={'allTasks'}
        text={t("home_page.all_task_title")}
      />
      {visibility.allTasks && renderTasks('all')}
      <TaskDetailDrawer
        isOpen={isDrawerOpen}
        taskId={selectedTaskId}
        onClose={handleCloseDrawer}
      />
    </>
  );
}